import { Table as ChakraTable, Box, TableProps as ChakraTableProps } from '@chakra-ui/react';
import { useMultiStyleConfig } from '@chakra-ui/system';
import { mergeWith } from '@chakra-ui/utils';
import PropTypes from 'prop-types';
import React from 'react';

import TableBody from './TableBody';
import { TableExperimentalContextProvider, useTableExperimentalContext } from './TableExperimentalContext';
import TableHeader from './TableHeader';
import TablePagination from './TablePagination';
import { TableProps, TableBodyProps } from './TableTypes';

import { forwardRef } from 'system';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TableExperimentalProps = TableProps<any>;

const propTypes = {
  data: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  columns: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  onRowSelectionChange: PropTypes.func,
  onSortingChange: PropTypes.func,
  enableSorting: PropTypes.bool,
  defaultSorting: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  disableRowHoverStyle: PropTypes.bool,
  isStickyHeader: PropTypes.bool,
  isStickyFirstColumn: PropTypes.bool
};

const defaultProps = {
  data: [],
  enableSorting: false,
  disableRowHoverStyle: false,
  isStickyHeader: false,
  isStickyFirstColumn: false
};

export type Props = {
  disableRowHoverStyle: TableExperimentalProps['disableRowHoverStyle'];
  isStickyHeader: TableExperimentalProps['isStickyHeader'];
  isStickyFirstColumn: TableExperimentalProps['isStickyFirstColumn'];
  sx: ChakraTableProps['sx'];
  tdCheckboxSx?: TableBodyProps['tdCheckboxSx'];
};

const Table = (props: Props): React.ReactElement => {
  const { disableRowHoverStyle, isStickyHeader, isStickyFirstColumn, sx, tdCheckboxSx, ...rest } = props;

  const { ref, isScrollLeftEdge, onRowClick, pagination } = useTableExperimentalContext();

  const themeStyles = useMultiStyleConfig('TableExperimental', {
    disableRowHoverStyle,
    isStickyHeader,
    isStickyFirstColumn,
    isScrollLeftEdge,
    onRowClick,
    pagination
  });

  return (
    <Box {...rest} __css={themeStyles.tableContainer} ref={ref} sx={mergeWith(themeStyles, sx)}>
      <ChakraTable>
        <TableHeader />
        <TableBody tdCheckboxSx={tdCheckboxSx} />
      </ChakraTable>
      {pagination && <TablePagination />}
    </Box>
  );
};

const TableExperimental = forwardRef(
  (props: TableExperimentalProps, ref: React.MutableRefObject<HTMLTableElement>): React.ReactElement => {
    const {
      data,
      columns,
      rowSelection,
      onRowClick,
      setRowSelection,
      onSortingChange,
      enableSorting,
      defaultSorting,
      disableRowHoverStyle,
      isStickyHeader,
      isStickyFirstColumn,
      sx,
      children,
      pagination,
      setPagination,
      pageCount,
      ...rest
    } = props;

    const contextProviderProps = {
      data,
      columns,
      rowSelection,
      onRowClick,
      setRowSelection,
      onSortingChange,
      defaultSorting,
      enableSorting,
      pagination,
      setPagination,
      pageCount,
      ref
    };

    const tableProps = {
      disableRowHoverStyle,
      isStickyHeader,
      isStickyFirstColumn,
      sx,
      ...rest
    };

    return (
      <TableExperimentalContextProvider {...contextProviderProps} ref={ref}>
        <Table {...tableProps} />
      </TableExperimentalContextProvider>
    );
  }
);

TableExperimental.displayName = 'TableExperimental';
TableExperimental.propTypes = propTypes;
TableExperimental.defaultProps = defaultProps;

export default TableExperimental;
